import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, type PostValue } from './client'

export type ProactiviteeTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  header?: string
  lead?: boolean
  any?: string[]
}

export type ProactiviteeShell = {
  pageTitle: string
  moduleUrl: string
  tabs: ProactiviteeTab[]
}

export const TABS = [
  'alarms',
  'company_settings',
  'nsclient',
  'email_templates',
  'agent_notification_templates',
  'triggers_settings',
  'quiet_hours',
  'email_server_settings',
] as const

export type ProactiviteeTabId = (typeof TABS)[number]

export function isProactiviteeTab(value: string): value is ProactiviteeTabId {
  return (TABS as readonly string[]).includes(value)
}

export function proactiviteeShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'shell'],
    queryFn: () => apiGet<ProactiviteeShell>('/settings/proactivitee'),
  })
}

export function companySettingsQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'company'],
    queryFn: () => apiGet<{ company_settings: Record<string, unknown> | null; moduleUrl: string }>('/settings/proactivitee/company-settings'),
  })
}

export function updateCompanySettings(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/company-settings', fields)
}

/**
 * `door`: `proactivitee` (usa lui) sau `securitee` - panoul Workstation
 * security din Settings > Securitee > Alarms (settings/Securitee::workstation_alarms),
 * aceleasi tabele, alte capitole.
 */
export function alarmsQuery(door = 'proactivitee') {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'alarms', door],
    queryFn: () => apiGet<Record<string, unknown>>('/settings/proactivitee/alarms' + (door === 'proactivitee' ? '' : '?door=' + door)),
  })
}

export function saveThresholds(fields: Record<string, PostValue>) {
  return apiPost<{ status?: string; message?: string }>('/settings/proactivitee/thresholds', fields)
}

export type AlarmSignal = {
  id: number
  name: string
  hint?: string
  chapter?: string
  dinamic?: number
  sign?: string
  watched?: boolean
  loaded?: boolean
  bands?: Array<{ flag: number; from?: unknown; to?: unknown; on?: boolean; dinamic?: number; factory?: boolean }>
}

export function alarmsMatrix(door: string, layer: string, group: number) {
  const q = new URLSearchParams({ door, layer, group: String(group) })
  return apiGet<{ signals: AlarmSignal[] }>('/settings/proactivitee/alarms/matrix?' + q.toString())
}

export function loadFactoryEmpty(fields: Record<string, PostValue>) {
  return apiPost<{ seeded?: number }>('/settings/proactivitee/alarms/factory', fields)
}

export function getAlarmsType(type: number, group: number, sp: number) {
  const q = new URLSearchParams()
  if (group > 0) q.set('group', String(group))
  if (sp > 0) q.set('sp', String(sp))
  const suffix = q.toString() ? '?' + q.toString() : ''
  return apiGet<{
    default: boolean
    alertType: Record<string, unknown> | null
    alarms: Array<Record<string, unknown>>
    inheritedFrom: string
    layer: string
  }>('/settings/proactivitee/alarms/type/' + type + suffix)
}

export function loadDefaultSettings(fields: Record<string, PostValue>) {
  return apiPost<{ loaded?: number }>('/settings/proactivitee/alarms/defaults', fields)
}

export function createAlarm(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean; id?: number }>('/settings/proactivitee/alarms', fields)
}

export function updateAlarm(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/alarms/update', fields)
}

export function changeAlarmStatus(alarm: number, activate: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/alarms/status', { alarm, activate })
}

export function removeAlarm(alarm: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/alarms/remove', { alarm })
}

export function checkAlarmValue(fields: Record<string, PostValue>) {
  return apiPost<{ valid?: string }>('/settings/proactivitee/alarms/check-value', fields)
}

export function createAlarmForm(type: number, group: number, sp: number) {
  const q = new URLSearchParams()
  if (group > 0) q.set('group', String(group))
  if (sp > 0) q.set('sp', String(sp))
  const suffix = q.toString() ? '?' + q.toString() : ''
  return apiGet<{
    group: number | null
    sp: number | null
    alarm: Record<string, unknown> | null
    triggers: Array<Record<string, unknown>>
    events: Array<Record<string, unknown>>
  }>('/settings/proactivitee/alarms/create-form/' + type + suffix)
}

export function editAlarmForm(id: number) {
  return apiGet<{
    alarm: Record<string, unknown>
    alarmType: Record<string, unknown> | null
    triggers: Array<Record<string, unknown>>
    events: Array<Record<string, unknown>>
    alarmTriggers: Record<string, number>
  }>('/settings/proactivitee/alarms/' + id + '/edit')
}

export function nsclientQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'nsclient'],
    queryFn: () => apiGet<{ nsclient_file: { name?: string } | null; service_provider: number; moduleUrl: string }>('/settings/proactivitee/nsclient'),
  })
}

export function uploadNsClient(body: FormData) {
  return apiPostForm<{ ok?: boolean; status?: string; message?: string }>('/settings/proactivitee/nsclient', body)
}

export function emailTemplatesQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'email-templates'],
    queryFn: () => apiGet<{ templates: Array<Record<string, unknown>>; variables: Array<Record<string, unknown>> }>('/settings/proactivitee/email-templates'),
  })
}

export function addEmailTemplate(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/email-templates', fields)
}

export function updateEmailTemplate(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/email-templates/update', fields)
}

export function deleteEmailTemplate(template: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/email-templates/delete', { template })
}

export function agentTemplatesQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'agent-templates'],
    queryFn: () => apiGet<{ templates: Array<Record<string, unknown>>; variables: Array<Record<string, unknown>> }>('/settings/proactivitee/agent-templates'),
  })
}

export function addAgentTemplate(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/agent-templates', fields)
}

export function updateAgentTemplate(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/agent-templates/update', fields)
}

export function deleteAgentTemplate(template: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/agent-templates/delete', { template })
}

export function triggersQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'triggers'],
    queryFn: () => apiGet<{ triggers: Array<Record<string, unknown>> }>('/settings/proactivitee/triggers'),
  })
}

export function createTrigger(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/triggers', fields)
}

export function deleteTrigger(trigger: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/triggers/delete', { trigger })
}

/**
 * Un fapt din `it_alarms_actions`, cu ce s-a bifat pe declansatorul deschis.
 * `action_key` alege casuta din dreapta, ca in `actionInput()` din portal.
 */
export type TriggerAction = {
  ID: number
  action_name?: string
  action_key?: string
  action_status?: number
  action_values?: string | null
  email_server_id?: number | string | null
}

export type TriggerForm = {
  trigger?: Record<string, unknown> | null
  actions: TriggerAction[]
  email_servers: Array<Record<string, unknown>>
}

export function triggerCreateFormQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'trigger-form', 'new'],
    queryFn: () => apiGet<TriggerForm>('/settings/proactivitee/triggers/form'),
  })
}

export function triggerFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'trigger-form', String(id)],
    queryFn: () => apiGet<TriggerForm>('/settings/proactivitee/triggers/' + id + '/form'),
  })
}

export function updateTrigger(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/triggers/update', fields)
}

export function standardCommandsQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'standard-commands'],
    queryFn: () => apiGet<{ commands: Array<Record<string, unknown>> }>('/settings/proactivitee/standard-commands'),
  })
}

export function emailServersQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'email-servers'],
    queryFn: () => apiGet<{ servers: Array<Record<string, unknown>> }>('/settings/proactivitee/email-servers'),
  })
}

export function createEmailServer() {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/email-servers', {})
}

export function saveEmailServer(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/email-servers/save', fields)
}

export function removeEmailServer(server_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/email-servers/remove', { server_id })
}

export function quietHoursQuery() {
  return queryOptions({
    queryKey: ['settings', 'proactivitee', 'quiet'],
    queryFn: () => apiGet<{
      windows: Array<Record<string, unknown>>
      daily: Record<string, unknown>
      timezone: string
      devices: Array<Record<string, unknown>>
      groups: Array<Record<string, unknown>>
      dayNames: Record<string, string>
      localNow: string
    }>('/settings/proactivitee/quiet-hours'),
  })
}

export function quietSave(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean; error?: string }>('/settings/proactivitee/quiet-hours', fields)
}

export function quietEnd(id: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/quiet-hours/end', { id })
}

export function quietDelete(id: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/quiet-hours/delete', { id })
}

export function quietDigestSave(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean; error?: string }>('/settings/proactivitee/quiet-hours/digest', fields)
}

export function createGroup(group_name: string) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/alarm-groups', { group_name })
}

export function updateGroup(group: number, group_name: string) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/alarm-groups/update', { group, group_name })
}

export function removeGroup(group: number) {
  return apiPost<{ ok?: boolean }>('/settings/proactivitee/alarm-groups/remove', { group })
}
